import { ReactElement, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import SidePanelContainer from '../controls/SidePanelContainer';
import { TOAST_MESSAGES } from '../constants/global-constants';
import { getUsedTags, updateAlert } from '../services/dashboard-service';
import { useData } from '../context/data.context';
import MultiSelectInputBox from '../components/MultiSelectInputBox';
import { Alert } from '../constants/telosair-interfaces';
import CustomTextArea from '../controls/CustomTextArea';

type FORMDATA = {
  comment: string;
};

type AddNotesModalProps = {
  handleClose: () => void;
  alert: Alert;
};

/**
 * Modal - AddNotes.
 * @param props - Properties of AddNotesModal.
 * @returns ReactElement.
 */
export default function AddNotesModal(props: AddNotesModalProps): ReactElement {
  const { handleClose, alert } = props;
  const tagInputRef = useRef<any>(null);
  const { updateAlertData } = useData();
  const [reasonChips, setReasonChips] = useState<string[]>([]);
  const [tagList, setTagList] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const methods = useForm<FORMDATA>({
    mode: 'all'
  });
  const { handleSubmit } = methods;
  useEffect(() => {
    if (alert.tags) {
      setReasonChips(alert.tags);
    }
  }, [alert]);

  /**
   * Handles API call for activities put API.
   * @param data - Formdata.
   */
  const handleFormSubmit = async (data: FORMDATA): Promise<void> => {
    const allChips = tagInputRef.current.forceAddChip();
    try {
      if (allChips.length === 0) {
        toast.warn('Please add a cause or a reason');
        return;
      }
      setLoading(true);
      const updatedAlert = await updateAlert(
        { tags: allChips, noteTitle: data.comment || null },
        alert.id
      );
      updateAlertData(updatedAlert.data);
      toast.success(TOAST_MESSAGES.added);
      setLoading(false);
      handleClose();
    } catch (e) {
      setLoading(false);
      toast.success(TOAST_MESSAGES.error);
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  /**
   * Function to get old used tags.
   */
  const fetchUsedTags = async (): Promise<void> => {
    try {
      const response = await getUsedTags();
      setTagList(response.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };

  useEffect(() => {
    fetchUsedTags();
  }, []);

  return (
    <SidePanelContainer closeModal={() => handleClose()}>
      <div className="pt-3 flex text-gray-500 dark:text-gray-300 text-lg">Add Notes</div>
      {/* <div className="pb-3 flex text-gray-500 dark:text-gray-500 text-sm">
       
      </div> */}
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          <MultiSelectInputBox
            label="Tags"
            availableOptions={tagList}
            ref={tagInputRef}
            chips={reasonChips}
            height={160}
            setChips={setReasonChips}
            acceptsSentence={false}
            backgroundColor="#12151F"
          />
          <CustomTextArea
            backgroundColor="#12151F"
            height={160}
            label="Comments"
            idname="comment"
          />
          <div
            style={{ width: 'inherit' }}
            className="p-5 fixed bottom-0 right-0 flex justify-center gap-3 bg-white dark:bg-container-600"
          >
            <div>
              <Button
                theme="secondary-color"
                size="sm"
                name="Discard"
                onClick={() => handleClose()}
                isBlockButton
              />
            </div>
            <Button
              theme="primary"
              size="sm"
              name="Save Changes"
              buttonType="submit"
              loading={loading}
              isBlockButton
            />
          </div>
        </form>
      </FormProvider>
    </SidePanelContainer>
  );
}
